import { type Action, type Dispatch, isAnyOf, type TypedStartListening, type UnknownAction } from '@reduxjs/toolkit';

import { accountsSelectors, accountsUtils } from '../../flux/accounts';
import type { ACCOUNTS_REDUCER_KEY } from '../../flux/accounts/accountsTypes';
import { getIdentityKeys } from '../../flux/core/resourceStoreTypes';
import type { DATE_REDUCER_KEY } from '../../flux/date/dateTypes';
import { type FEATURE_FLAGS_REDUCER_KEY, featureFlagsSelectors } from '../../flux/feature-flags';
import { investmentHoldingsV2Actions } from '../../flux/investment-holdings-v2';
import { transactionsActions, transactionsSelectors } from '../../flux/transactions';
import type { TRANSACTIONS_REDUCER_KEY, TransactionRecord } from '../../flux/transactions/transactionsTypes';
import type RootState from '../../utils/redux-store/rootState';

// `getResourcesById` reads only the transaction store, but is typed against the wider slice the
// transactions selectors share.
type RequiredState = Pick<
  RootState,
  | typeof TRANSACTIONS_REDUCER_KEY
  | typeof ACCOUNTS_REDUCER_KEY
  | typeof FEATURE_FLAGS_REDUCER_KEY
  | typeof DATE_REDUCER_KEY
>;
type AppStartListening = TypedStartListening<RequiredState, Dispatch<Action>>;

type HoldingRef = { accountId: string; localSecurityId: string };

const holdingRefKey = (ref: HoldingRef): string => `${ref.accountId}:${ref.localSecurityId}`;

/**
 * The holding an investment transaction contributes to, or null when there is nothing for QCS to
 * rebuild: a cash flow/invoice transaction, or an investment transaction that names no security
 * (an income or misc-expense entry) or is not yet assigned to an account.
 */
const toHoldingRef = (transaction?: TransactionRecord | null): HoldingRef | null => {
  if (transaction?.type !== 'INVESTMENT') return null;
  const { accountId, localSecurityId } = transaction;
  return accountId && localSecurityId ? { accountId, localSecurityId } : null;
};

/**
 * QCS logs a warning and does nothing for anything but a manual investment account, so don't ask.
 * Matches its own test: `type == INVESTMENT && (institutionLoginId == null || == "0")`.
 */
const isManualInvestmentAccount = (state: RequiredState, accountId: string): boolean => {
  const account = accountsSelectors.getAccountById(state, accountId);
  return account?.type === 'INVESTMENT' && accountsUtils.isAccountManual(account);
};

const asRecords = (payload: unknown): TransactionRecord[] =>
  Array.isArray(payload) ? (payload as TransactionRecord[]) : [payload as TransactionRecord];

/**
 * `id` once persisted, `clientId` while the create is still in flight. Both are kept because a
 * request and its response do not always agree on which one is populated.
 */
const identityKeysOf = (payload: unknown): string[] =>
  asRecords(payload).flatMap((resource) => getIdentityKeys(resource));

const isSaveRequest = isAnyOf(
  transactionsActions.createTransaction,
  transactionsActions.updateTransaction,
  transactionsActions.deleteTransaction,
  transactionsActions.batchTransactions,
);

const isSaveSuccess = isAnyOf(
  transactionsActions.createTransactionSuccess,
  transactionsActions.updateTransactionSuccess,
  transactionsActions.deleteTransactionSuccess,
  transactionsActions.batchTransactionsSuccess,
);

const isSaveFailure = isAnyOf(
  transactionsActions.createTransactionFailure,
  transactionsActions.updateTransactionFailure,
  transactionsActions.deleteTransactionFailure,
  transactionsActions.batchTransactionsFailure,
);

/**
 * Setup holdingSyncFromTransactions listener.
 *
 * SIMPL-33607 POC. QCS can rebuild a holding from the investment transactions it holds for a
 * security, so when Simplifi adds, deletes, or modifies an investment transaction we ask it to
 * redo that holding rather than leaving the two out of step.
 *
 * The sync has to run *after* QCS has the edit, otherwise it recomputes from the transactions as
 * they were, so the listener starts on the request action and waits for the matching success.
 * Starting there is also what makes the security-change case work: the pre-edit transaction is
 * still in `getOriginalState()`, so a transaction moved from one security to another syncs the
 * holding it left as well as the one it joined.
 *
 * Only manual investment accounts sync: QCS refuses everything else, and connected-account
 * behavior has not been designed yet.
 *
 * Gated on the `investmentsSyncHoldingsRelease` flag (LaunchDarkly key
 * `investments.sync-holdings.release`) so it can go out to targeted test users.
 *
 * @param startAppListening - The platform-specific startAppListening function
 */
export const setupHoldingSyncFromTransactionsListener = (startAppListening: AppStartListening): void => {
  startAppListening({
    matcher: isSaveRequest,
    async effect(action, listenerApi) {
      if (!featureFlagsSelectors.getFeatureFlag(listenerApi.getOriginalState(), 'investmentsSyncHoldingsRelease')) {
        return;
      }

      const refs = new Map<string, HoldingRef>();
      // Filtered per ref rather than once for the transaction: an edit that moves a transaction
      // between accounts produces one ref per account, and only some of them may be manual.
      const addRef = (ref: HoldingRef | null) => {
        if (ref && isManualInvestmentAccount(listenerApi.getState(), ref.accountId)) {
          refs.set(holdingRefKey(ref), ref);
        }
      };

      // Pre-edit holdings, read before the optimistic reducer replaces them. Deletes rely on this
      // too, since a delete payload need only carry enough of the transaction to identify it.
      const previousTransactions = transactionsSelectors.getResourcesById(listenerApi.getOriginalState());
      const requested = asRecords(action.payload);
      requested.forEach((resource) => {
        getIdentityKeys(resource).forEach((key) => addRef(toHoldingRef(previousTransactions.get(key))));
        addRef(toHoldingRef(resource));
      });

      if (refs.size === 0) return;

      const savedKeys = new Set(identityKeysOf(action.payload));
      const isThisSave = (candidate: UnknownAction): boolean =>
        (isSaveSuccess(candidate) || isSaveFailure(candidate)) &&
        identityKeysOf(candidate.payload).some((key) => savedKeys.has(key));

      // Deliberately untimed: a save that QCS can't reach yet is postponed and retried, and the
      // sync is still the right thing to do whenever that retry lands.
      const [confirmation] = await listenerApi.take(isThisSave);
      // QCS rejected the edit, so the transactions are unchanged and so is the holding.
      if (!isSaveSuccess(confirmation)) return;

      refs.forEach((ref) => listenerApi.dispatch(investmentHoldingsV2Actions.syncHoldingFromTransactions(ref)));
    },
  });
};
